import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { machineryService } from '../../../services/machineryService';
import Toast from '../../../components/Common/Toast';
import { formatDateLocaleLima } from '../../../utils/dateUtils';
import { normalizeTime } from '../../../utils/dateUtils';
import { formatBitacoraReading } from '../../../utils/bitacoraFormat';

const MAINTENANCE_EVENT_TYPES = [
  'Inicio de mantenimiento preventivo',
  'Fin de mantenimiento preventivo',
  'Inicio de mantenimiento de emergencia',
  'Fin de mantenimiento de emergencia',
  'Mantenimiento'
];

const MaintenanceHistory = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [machineId, setMachineId] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [machines, setMachines] = useState([]);

  useEffect(() => {
    loadVehiclesAndMachines();
  }, []);

  useEffect(() => {
    loadHistory();
  }, [dateFrom, dateTo, vehicleId, machineId]);

  const loadVehiclesAndMachines = async () => {
    try {
      const res = await machineryService.getMachineryFormData();
      if (res.success && res.data) {
        setVehicles(res.data.vehicles || []);
        setMachines(res.data.machines || []);
      }
    } catch (e) {
      // ignore
    }
  };

  const loadHistory = async () => {
    setLoading(true);
    try {
      const params = { event_type: MAINTENANCE_EVENT_TYPES.join(',') };
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      if (vehicleId) params.vehicleId = vehicleId;
      if (machineId) params.machineId = machineId;
      const response = await machineryService.getEvents(params);
      if (response.success && Array.isArray(response.data)) {
        setEvents(response.data);
      } else {
        setToast({ isOpen: true, message: response.message || 'Error al cargar historial', type: 'error' });
      }
    } catch (error) {
      setToast({ isOpen: true, message: 'Error al cargar historial de mantenimientos', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const formatReading = (ev) => {
    const parts = [];
    if (ev.hours != null && ev.hours !== '') parts.push(`${formatBitacoraReading(ev.hours)} h`);
    if (ev.kilometers != null && ev.kilometers !== '') parts.push(`${formatBitacoraReading(ev.kilometers)} km`);
    return parts.length ? parts.join(' · ') : '—';
  };

  const getEventBadgeClass = (eventType) => {
    if (!eventType) return 'bg-gray-100 text-gray-800';
    if (eventType.includes('Inicio') && eventType.includes('preventivo')) return 'bg-blue-100 text-blue-800';
    if (eventType.includes('Fin') && eventType.includes('preventivo')) return 'bg-green-100 text-green-800';
    if (eventType.includes('Inicio') && eventType.includes('emergencia')) return 'bg-amber-100 text-amber-800';
    if (eventType.includes('Fin') && eventType.includes('emergencia')) return 'bg-orange-100 text-orange-800';
    return 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <Link to="/machinery/maintenance" className="text-sm text-teal-600 hover:text-teal-800 mb-1 inline-block">← Alertas de mantenimiento</Link>
          <h1 className="text-2xl font-bold text-gray-800">Historial de mantenimientos</h1>
          <p className="text-gray-600 mt-1">Eventos de inicio y fin de mantenimiento preventivo y de emergencia registrados en la bitácora.</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vehículo</label>
            <select
              value={vehicleId}
              onChange={(e) => setVehicleId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
            >
              <option value="">Todos</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>{v.code} — {v.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Máquina</label>
            <select
              value={machineId}
              onChange={(e) => setMachineId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
            >
              <option value="">Todas</option>
              {machines.map((m) => (
                <option key={m.id} value={m.id}>{m.code} — {m.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 py-12 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
          <p className="mt-2 text-gray-600">Cargando historial...</p>
        </div>
      ) : events.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <p className="text-gray-500">No hay registros de mantenimiento en el período seleccionado.</p>
          <Link to="/machinery/maintenance" className="mt-4 inline-block text-teal-600 hover:text-teal-800 font-medium">Ver alertas de mantenimiento</Link>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Fecha</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Hora</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Tipo de evento</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Equipo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Lectura</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Operador</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Notas</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {events.map((ev) => (
                  <tr key={ev.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {ev.event_date ? formatDateLocaleLima(ev.event_date) : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {ev.event_time ? normalizeTime(ev.event_time) : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEventBadgeClass(ev.event_type)}`}>
                        {ev.event_type || '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {ev.machine_id ? (ev.machine_code ? `${ev.machine_code} — ${ev.machine_name || ''}` : `Máquina #${ev.machine_id}`) : ev.vehicle_id ? (ev.vehicle_code ? `${ev.vehicle_code} — ${ev.vehicle_name || ''}` : `Vehículo #${ev.vehicle_id}`) : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{formatReading(ev)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{ev.operator || '—'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate" title={ev.notes}>{ev.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Toast isOpen={toast.isOpen} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, isOpen: false })} />
    </div>
  );
};

export default MaintenanceHistory;
